import { format } from 'date-fns';
import { Task, Category } from '../../types';
import { Button } from '../base/Button';

interface TaskListProps {
  tasks: Task[];
  categories: Category[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
}

export const TaskList = ({ tasks, categories, onEditTask, onDeleteTask }: TaskListProps) => {
  const getCategoryName = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)?.name || 'Unknown';
  };

  const getCategoryColor = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)?.color || '#6B7280';
  };

  const mainTasks = tasks.filter(task => !task.parentId);
  const subTasks = tasks.filter(task => task.parentId);

  const getTaskDependencies = (task: Task) => {
    return task.dependencies
      .map(depId => tasks.find(t => t.id === depId)?.name)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Task List</h2>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Task
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dependencies
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {mainTasks.map((task) => {
              const taskSubTasks = subTasks.filter(sub => sub.parentId === task.id);
              
              return (
                <div key={task.id}>
                  {/* Main Task */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getCategoryColor(task.categoryId) }}
                        />
                        <div>
                          <div className="font-medium text-gray-900">{task.name}</div>
                          {task.description && (
                            <div className="text-sm text-gray-500">{task.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {getCategoryName(task.categoryId)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{format(task.startDate, 'MMM d, yyyy')}</div>
                      <div className="text-gray-500">to {format(task.endDate, 'MMM d, yyyy')}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              width: `${task.progress}%`,
                              backgroundColor: getCategoryColor(task.categoryId)
                            }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{task.progress}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {getTaskDependencies(task) || 'None'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => onEditTask(task)}
                        >
                          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => onDeleteTask(task.id)}
                        >
                          <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>

                  {/* Sub Tasks */}
                  {taskSubTasks.map((subTask) => (
                    <tr key={subTask.id} className="hover:bg-gray-50 bg-gray-25">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 ml-6">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: getCategoryColor(subTask.categoryId) }}
                          />
                          <div>
                            <div className="text-sm text-gray-700">{subTask.name}</div>
                            {subTask.description && (
                              <div className="text-xs text-gray-500">{subTask.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {getCategoryName(subTask.categoryId)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div>{format(subTask.startDate, 'MMM d, yyyy')}</div>
                        <div className="text-gray-500">to {format(subTask.endDate, 'MMM d, yyyy')}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full"
                              style={{ 
                                width: `${subTask.progress}%`,
                                backgroundColor: getCategoryColor(subTask.categoryId)
                              }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{subTask.progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {getTaskDependencies(subTask) || 'None'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onEditTask(subTask)}
                          >
                            <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => onDeleteTask(subTask.id)}
                          >
                            <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </div>
              );
            })}
          </tbody>
        </table>
        
        {tasks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No tasks found. Create your first task to get started.
          </div>
        )}
      </div>
    </div>
  );
};